<!-- src/lib/components/CardPanel.svelte -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';
  import { fly, fade } from 'svelte/transition';
  import type { Label, Comment, AppEvent } from '../types.ts';
  import { marked } from 'marked';
  import { timeAgo } from '../format-time.ts';
  import { platform } from '../platform.ts';
  import StatusIcon from './StatusIcon.svelte';
  import { agentStore } from '../stores/agents.svelte.ts';

  interface Persona {
    id: string;
    name: string;
    preferred_tier: string;
  }

  // Configure marked for safe, readable rendering
  marked.setOptions({ breaks: true, gfm: true });

  // Action to focus an element on mount (avoids a11y_autofocus warning)
  function focusOnMount(node: HTMLElement) {
    node.focus();
  }

  interface Props {
    cardId: string | null;
    onClose: () => void;
    onOpenAgent?: (agentId: string) => void;
    focusMode?: boolean;
    onToggleFocus?: () => void;
    /** Called when the user asks to open the linked worktree in the file
     *  explorer. Parent (App.svelte) sets the side-pane root and opens it. */
    onOpenWorktree?: (path: string) => void;
  }
  let { cardId, onClose, onOpenAgent, focusMode = false, onToggleFocus, onOpenWorktree }: Props = $props();

  let editTitle   = $state('');
  let editDesc    = $state('');
  let editPri     = $state('none');
  let editDue     = $state('');
  let editingDesc = $state(false);

  function sanitizeHtml(html: string): string {
    return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }

  const renderedDesc: string = $derived(
    editDesc ? sanitizeHtml(marked.parse(editDesc, { async: false }) as string) : ''
  );

  let saveStatus = $state<'idle' | 'saving' | 'saved' | 'error'>('idle');
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;

  let projectLabels   = $state<Label[]>([]);
  let cardLabelIds    = $state(new Set<string>());
  let labelsLoading   = $state(false);

  let comments          = $state<Comment[]>([]);
  let commentsLoading   = $state(false);

  // ── Activity timeline (Round 14, refinement #28) ──────────────
  let activityEvents    = $state<AppEvent[]>([]);
  let activityLoading   = $state(false);
  let newCommentBody    = $state('');
  let submittingComment = $state(false);

  const priorities = [
    { value: 'urgent', label: 'Urgent', color: 'oklch(0.62 0.26 25)' },
    { value: 'high',   label: 'High',   color: 'oklch(0.78 0.16 85)' },
    { value: 'medium', label: 'Medium', color: 'oklch(0.58 0.22 265)' },
    { value: 'low',    label: 'Low',    color: 'rgba(255,255,255,0.3)' },
    { value: 'none',   label: 'None',   color: 'rgba(255,255,255,0.1)' },
  ];

  let card = $derived(
    cardId
      ? Object.values(pmStore.boardDetails)
          .flatMap(bd => bd.columns.flatMap(c => c.cards))
          .find(c => c.id === cardId) ?? null
      : null
  );

  let boardDetail = $derived(
    Object.values(pmStore.boardDetails).find(bd =>
      bd.columns.some(c => c.cards.some(card => card.id === cardId))
    ) ?? null
  );

  let currentColumn = $derived(
    boardDetail?.columns.find(c => c.cards.some(card => card.id === cardId)) ?? null
  );

  let projectName = $derived(
    pmStore.projects.find(p => p.id === card?.project_id)?.name ?? ''
  );

  $effect(() => {
    if (card) {
      editTitle       = card.title;
      editDesc        = card.description ?? '';
      editPri         = card.priority;
      editDue         = card.due_date ?? '';
      pendingCriteria = card.loop_criteria ?? '';
    }
  });

  $effect(() => {
    if (!cardId || !card) return;
    const id        = cardId;
    const projectId = card.project_id;

    labelsLoading = true;
    api<Label[]>(`/api/v1/projects/${projectId}/labels`)
      .then(l => { projectLabels = l; })
      .catch(() => {})
      .finally(() => { labelsLoading = false; });

    api<Label[]>(`/api/v1/cards/${id}/labels`)
      .then(l => {
        pmStore.cardLabels[id] = l;
        cardLabelIds = new Set(l.map(x => x.id));
      })
      .catch(() => {});

    commentsLoading = true;
    api<Comment[]>(`/api/v1/cards/${id}/comments`)
      .then(c => { comments = c; pmStore.cardComments[id] = c; })
      .catch(() => {})
      .finally(() => { commentsLoading = false; });

    // Activity timeline — fetch all events for this card id
    activityLoading = true;
    api<AppEvent[]>(`/api/v1/agents/${id}/events`)
      .then(evts => {
        // Filter to PM events only + reverse so newest last (timeline reads top-down)
        const PM_EVENTS = new Set([
          'card_created', 'card_updated', 'card_moved', 'card_deleted',
          'card_label_added', 'card_label_removed',
          'comment_added', 'comment_deleted',
          'task_queued', 'task_started', 'task_completed', 'task_failed', 'task_cancelled',
        ]);
        activityEvents = evts
          .filter(e => PM_EVENTS.has(e.event_type))
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      })
      .catch(() => { activityEvents = []; })
      .finally(() => { activityLoading = false; });
  });

  async function autoSave() {
    if (!cardId || saveStatus === 'saving') return;
    saveStatus = 'saving';
    try {
      await api(`/api/v1/cards/${cardId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: editTitle,
          description: editDesc || null,
          priority: editPri,
          due_date: editDue || null,
        }),
      });
      saveStatus = 'saved';
      setTimeout(() => { saveStatus = 'idle'; }, 2000);
    } catch {
      saveStatus = 'error';
      setTimeout(() => { saveStatus = 'idle'; }, 3000);
    }
  }

  function scheduleSave() {
    if (saveTimeout) clearTimeout(saveTimeout);
    saveTimeout = setTimeout(autoSave, 600);
  }

  async function changeColumn(newColumnId: string) {
    if (!cardId || newColumnId === currentColumn?.id) return;
    saveStatus = 'saving';
    try {
      await api(`/api/v1/cards/${cardId}`, {
        method: 'PATCH',
        body: JSON.stringify({ column_id: newColumnId }),
      });
      // Update pmStore reactively: move card from old column to new column
      const bd = boardDetail;
      if (bd) {
        const cardObj = card;
        for (const col of bd.columns) {
          const idx = col.cards.findIndex(c => c.id === cardId);
          if (idx !== -1) {
            col.cards.splice(idx, 1);
            break;
          }
        }
        const targetCol = bd.columns.find(col => col.id === newColumnId);
        if (targetCol && cardObj) {
          targetCol.cards.push(cardObj);
        }
      }
      saveStatus = 'saved';
      setTimeout(() => { saveStatus = 'idle'; }, 2000);
    } catch {
      saveStatus = 'error';
      setTimeout(() => { saveStatus = 'idle'; }, 3000);
    }
  }

  async function toggleLabel(label: Label) {
    if (!cardId) return;
    try {
      if (cardLabelIds.has(label.id)) {
        await api(`/api/v1/cards/${cardId}/labels/${label.id}`, { method: 'DELETE' });
        cardLabelIds.delete(label.id);
        cardLabelIds = new Set(cardLabelIds);
      } else {
        await api(`/api/v1/cards/${cardId}/labels/${label.id}`, { method: 'PUT', body: '{}' });
        cardLabelIds.add(label.id);
        cardLabelIds = new Set(cardLabelIds);
      }
    } catch { /* revert not needed — server is source of truth, panel will re-fetch on close/reopen */ }
  }

  async function submitComment() {
    if (!cardId || !newCommentBody.trim()) return;
    submittingComment = true;
    try {
      const comment = await api<Comment>(`/api/v1/cards/${cardId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body: newCommentBody.trim() }),
      });
      comments = [...comments, comment];
      newCommentBody = '';
    } catch { /* silent */ } finally {
      submittingComment = false;
    }
  }

  async function deleteComment(commentId: string) {
    if (!cardId) return;
    try {
      await api(`/api/v1/comments/${commentId}`, { method: 'DELETE' });
      comments = comments.filter(c => c.id !== commentId);
    } catch { /* silent — comment stays visible if delete fails */ }
  }

  function formatCommentTime(iso: string): string {
    const d    = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  let pendingCriteria = $state('');

  async function toggleLoopMode(enabled: boolean) {
    await api(`/api/v1/cards/${card!.id}/loop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled, max_iterations: card!.loop_max || 5, criteria: card!.loop_criteria }),
    });
  }

  async function updateLoopMax(max: number) {
    await api(`/api/v1/cards/${card!.id}/loop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: card!.loop_mode, max_iterations: max, criteria: card!.loop_criteria }),
    });
  }

  async function saveCriteria() {
    await api(`/api/v1/cards/${card!.id}/loop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: card!.loop_mode, max_iterations: card!.loop_max, criteria: pendingCriteria }),
    });
  }

  // ── Worktree link ────────────────────────────────────────────────────
  // Calls POST /api/v1/cards/{id}/worktree to set or clear the card's
  // linked git worktree path. The returned card is then written back
  // into pmStore so the UI updates without a page reload.
  async function setWorktree(path: string | null) {
    if (!card) return;
    try {
      const updated = await api<typeof card>(`/api/v1/cards/${card.id}/worktree`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path }),
      });
      // Write the updated card back into any loaded board details so
      // the CardPanel's reactive read (via `card = cardId && find(...)`)
      // picks up the new worktree_path immediately.
      for (const bd of Object.values(pmStore.boardDetails)) {
        for (const col of bd.columns) {
          const i = col.cards.findIndex(c => c.id === updated.id);
          if (i !== -1) col.cards[i] = updated;
        }
      }
    } catch (e) {
      console.warn('setWorktree failed:', e);
    }
  }

  // Folder picker — same Tauri-or-prompt pattern as FileExplorer.
  // Routes through lib/platform so the Tauri check happens at the
  // right boundary. The naive "await import @tauri-apps/api; await
  // invoke" pattern hangs the browser when there's no Tauri host.
  async function pickWorktree() {
    let path: string | null = await platform.selectDirectory();
    if (path == null) {
      const entered = window.prompt(
        'Absolute path to the worktree directory:',
        card?.worktree_path ?? ''
      );
      path = entered && entered.trim() ? entered.trim() : null;
    }
    if (path) {
      await setWorktree(path);
    }
  }

  let startingWork     = $state(false);
  let personas         = $state<Persona[]>([]);
  let selectedPersonaId = $state('');

  $effect(() => {
    const wsId = card ? pmStore.projects.find(p => p.id === card?.project_id)?.workspace_id : null;
    if (!wsId) return;
    api<Persona[]>(`/api/v1/workspaces/${wsId}/personas`)
      .then(p => { personas = p; })
      .catch(() => {});
  });

  async function startWork() {
    if (!cardId) return;
    startingWork = true;
    try {
      const result = await api<{ agent_id: string }>(`/api/v1/cards/${cardId}/start`, {
        method: 'POST',
        body: JSON.stringify({
          prompt: editTitle,
          persona_id: selectedPersonaId || undefined,
        }),
      });
      // Optimistically update the card in pmStore
      for (const bd of Object.values(pmStore.boardDetails)) {
        for (const col of bd.columns) {
          const c = col.cards.find(c => c.id === cardId);
          if (c) { c.agent_id = result.agent_id; break; }
        }
      }
      onOpenAgent?.(result.agent_id);
    } catch { /* silent */ } finally {
      startingWork = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }

  // ── Round 14: Drawer tabs ──────────────────────────────────────
  type DrawerTab = 'details' | 'discussion' | 'runs' | 'activity' | 'transcript';
  let drawerTab = $state<DrawerTab>('details');

  const runEvents = $derived(
    [...activityEvents]
      .filter((event) => ['task_queued', 'task_started', 'task_completed', 'task_failed', 'task_cancelled'].includes(event.event_type))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  );

  const liveOutput = $derived(
    card?.agent_id ? (agentStore.outputs[card.agent_id] ?? []).join('') : ''
  );

  // Reset tab when card changes
  $effect(() => {
    if (cardId) drawerTab = 'details';
  });

  // Improvement #24: agent provenance — true when this card is assigned to a persona
  const isAgentCard = $derived(card?.assignee_kind === 'persona');

  // ── Drawer resize (Round 4 split-view) ──────────────────────────────
  //
  // The drawer is a right-side split-view rather than a modal: the
  // board behind it stays interactive. We persist the width so the
  // user's preferred drawer size carries across sessions.

  const MIN_W = 360;
  const MAX_W = 820;
  const DEFAULT_W = 460;

  function loadWidth(): number {
    try {
      const v = localStorage.getItem('agentboard.cardPanelWidth');
      if (!v) return DEFAULT_W;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? Math.min(MAX_W, Math.max(MIN_W, n)) : DEFAULT_W;
    } catch {
      return DEFAULT_W;
    }
  }

  let drawerWidth = $state<number>(loadWidth());
  let resizing    = $state(false);

  function onResizeStart(e: MouseEvent) {
    e.preventDefault();
    resizing = true;
    const startX = e.clientX;
    const startW = drawerWidth;
    const move = (ev: MouseEvent) => {
      // Drag left-edge left → drawer grows. (We anchor to the right.)
      const dx = startX - ev.clientX;
      drawerWidth = Math.min(MAX_W, Math.max(MIN_W, startW + dx));
    };
    const up = () => {
      resizing = false;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      try { localStorage.setItem('agentboard.cardPanelWidth', String(drawerWidth)); } catch {}
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  // Click-outside-to-close. Uses pointerdown so it fires before
  // focus moves. Ignore clicks inside .panel itself.
  function onWindowPointerDown(e: PointerEvent) {
    if (!cardId) return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.card-panel-drawer')) return;
    // Ignore clicks inside any card on the board — those open a DIFFERENT
    // card, which should replace the drawer contents, not close it.
    if (target.closest('.card-item')) return;
    // Ignore clicks inside toolbar/chrome that already manage their own state.
    if (target.closest('.nav-rail, .nav-sidebar')) return;
    onClose();
  }
</script>

<svelte:window onkeydown={handleKeydown} onpointerdown={onWindowPointerDown} />

{#if cardId && card}
  <aside
    class="panel card-panel-drawer"
    class:resizing
    style="width: {drawerWidth}px"
    transition:fly={{ x: 500, duration: 220, opacity: 1 }}
  >
    <!-- Left-edge drag handle for resizing -->
    <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
    <div
      class="resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize card drawer"
      onmousedown={onResizeStart}
    ></div>
    <header>
      <div class="header-left">
        <div class="breadcrumb">
          <span class="breadcrumb-project">{projectName}</span>
          {#if projectName}<span class="breadcrumb-sep"> › </span>{/if}
          <span class="breadcrumb-column">{currentColumn?.name ?? 'No column'}</span>
          <span class="breadcrumb-sep"> · </span>
          <span class="breadcrumb-id">{card.human_id ?? '#' + card.id.slice(-4)}</span>
        </div>
      </div>
      <div class="header-right">
        {#if saveStatus === 'saving'}
          <span class="save-status saving" transition:fade={{ duration: 150 }}>·· saving</span>
        {:else if saveStatus === 'saved'}
          <span class="save-status saved" transition:fade={{ duration: 150 }}>✓ saved</span>
        {:else if saveStatus === 'error'}
          <span class="save-status error" transition:fade={{ duration: 150 }}>! failed</span>
        {/if}
        {#if onToggleFocus}
          <button class="focus-btn" onclick={onToggleFocus} title="Toggle focus mode (F)">
            {focusMode ? '⊞' : '⊡'}
          </button>
        {/if}
        <button class="close-btn" onclick={onClose}>✕ Close</button>
      </div>
    </header>

    <!-- Round 14: drawer tab bar -->
    <nav class="drawer-tabs">
      <button class="drawer-tab" class:active={drawerTab === 'details'} onclick={() => { drawerTab = 'details'; }}>Details</button>
      <button class="drawer-tab" class:active={drawerTab === 'discussion'} onclick={() => { drawerTab = 'discussion'; }}>
        <span>Discussion</span>
        {#if comments.length > 0}
          <span class="tab-count">{comments.length}</span>
        {/if}
      </button>
      <button class="drawer-tab" class:active={drawerTab === 'runs'} onclick={() => { drawerTab = 'runs'; }}>
        <span>Runs</span>
        {#if card.agent_id}
          <span class="tab-dot running-dot" aria-hidden="true"></span>
        {/if}
      </button>
      <button class="drawer-tab" class:active={drawerTab === 'activity'} onclick={() => { drawerTab = 'activity'; }}>Activity</button>
      <button class="drawer-tab" class:active={drawerTab === 'transcript'} onclick={() => { drawerTab = 'transcript'; }}>Transcript</button>
    </nav>

    <div class="panel-body">
    {#if drawerTab === 'details'}
      {#if isAgentCard}
        <div class="ai-assigned-badge">
          <span>🤖</span>
          <span>AI-assigned</span>
        </div>
      {/if}
      <div class="field">
        <label for="card-title">Title</label>
        <input
          id="card-title"
          class="input-field"
          bind:value={editTitle}
          onblur={autoSave}
        />
      </div>

      <div class="field">
        <div class="desc-header">
          <label for="card-desc">Description</label>
          {#if editingDesc}
            <span class="desc-hint">Markdown supported · Tab to preview</span>
          {:else}
            <span class="desc-hint">Click to edit</span>
          {/if}
        </div>
        {#if editingDesc}
          <textarea
            id="card-desc"
            class="textarea-field markdown-textarea"
            bind:value={editDesc}
            rows={8}
            placeholder="Add details…&#10;&#10;Supports **bold**, *italic*, `code`, lists, headings, and more."
            onblur={() => { editingDesc = false; autoSave(); }}
            use:focusOnMount
          ></textarea>
        {:else}
          <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
          <div
            class="markdown-view"
            class:empty={!editDesc}
            onclick={() => { editingDesc = true; }}
            role="button"
            tabindex="0"
            onkeydown={(e) => { if (e.key === 'Enter') editingDesc = true; }}
          >
            {#if editDesc}
              {@html renderedDesc}
            {:else}
              <span class="placeholder">Add details… (click to edit)</span>
            {/if}
          </div>
        {/if}
      </div>

      <div class="field">
        <div class="field-label">Priority</div>
        <div class="priority-picker">
          {#each priorities as p}
            <button
              type="button"
              class="priority-option"
              class:selected={editPri === p.value}
              style="--dot-color: {p.color}"
              onclick={() => { editPri = p.value; autoSave(); }}
            >
              <span class="p-dot" style="background: {p.color}"></span>
              {p.label}
            </button>
          {/each}
        </div>
      </div>

      <div class="field">
        <label for="card-due">Due Date</label>
        <input
          id="card-due"
          type="date"
          class="input-field date-input"
          bind:value={editDue}
          onchange={autoSave}
        />
      </div>

      {#if boardDetail && boardDetail.columns.length > 0}
        <div class="field">
          <div class="field-label">Status</div>
          <div class="column-picker">
            {#each boardDetail.columns as col}
              <button
                type="button"
                class="column-option"
                class:selected={col.id === currentColumn?.id}
                onclick={() => changeColumn(col.id)}
              >
                {col.name}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if !labelsLoading && projectLabels.length > 0}
        <div class="field">
          <div class="field-label">Labels</div>
          <div class="label-picker">
            {#each projectLabels as label}
              <button
                type="button"
                class="label-option"
                class:active={cardLabelIds.has(label.id)}
                style="--label-color: {label.color}"
                onclick={() => toggleLabel(label)}
              >
                <span class="label-swatch" style="background: {label.color}"></span>
                {label.name}
                {#if cardLabelIds.has(label.id)}<span class="label-check">✓</span>{/if}
              </button>
            {/each}
          </div>
        </div>
      {:else if !labelsLoading}
        <div class="field">
          <div class="field-label">Labels</div>
          <p class="empty-hint">No labels in this project yet.</p>
        </div>
      {/if}

      {#if card.agent_id}
        <div class="field">
          <div class="field-label">Agent</div>
          <button class="agent-link" onclick={() => onOpenAgent?.(card!.agent_id!)}>
            <span class="agent-pulse"></span>
            <span>{card.agent_id.slice(0, 16)}…</span>
            <span class="snoop-arrow">→ Snoop</span>
          </button>
        </div>
      {:else}
        {#if personas.length > 0}
          <div class="field">
            <label for="persona-select">Persona</label>
            <select id="persona-select" class="persona-select input-field" bind:value={selectedPersonaId}>
              <option value="">Default agent</option>
              {#each personas as p}
                <option value={p.id}>{p.name}</option>
              {/each}
            </select>
          </div>
        {/if}
        <button class="start-work-btn" onclick={startWork} disabled={startingWork}>
          <span class="sw-icon">{startingWork ? '…' : '▶'}</span>
          <span>Start Work</span>
          <span class="sw-hint">Spawn agent · {selectedPersonaId ? (personas.find(p => p.id === selectedPersonaId)?.name ?? 'Custom') : 'Claude Code'}</span>
        </button>
      {/if}

      <!-- Loop mode toggle -->
      <div class="loop-section">
        <label class="loop-toggle-label">
          <input
            type="checkbox"
            class="loop-checkbox"
            checked={card.loop_mode}
            onchange={(e) => toggleLoopMode((e.target as HTMLInputElement).checked)}
          />
          <span class="loop-label-text">Loop until done</span>
          {#if card.loop_mode}
            <span class="loop-count-badge">{card.loop_count}/{card.loop_max}</span>
          {/if}
        </label>
        {#if card.loop_mode}
          <div class="loop-details">
            <label class="loop-field-label" for="loop-max-input">Max iterations</label>
            <input
              id="loop-max-input"
              type="number"
              class="loop-max-input"
              value={card.loop_max}
              min="1"
              max="20"
              onchange={(e) => updateLoopMax(parseInt((e.target as HTMLInputElement).value) || 5)}
            />
            <label class="loop-field-label" for="loop-criteria-input">Stop when…</label>
            <textarea
              id="loop-criteria-input"
              class="loop-criteria-input"
              placeholder="Describe acceptance criteria (e.g. 'all tests pass and no lint errors')"
              value={pendingCriteria}
              oninput={(e) => pendingCriteria = (e.target as HTMLTextAreaElement).value}
              onblur={() => saveCriteria()}
              rows="3"
            ></textarea>
          </div>
        {/if}
      </div>

      <!-- Worktree link — ties the card to a specific directory so the
           file explorer + git panels auto-focus on the card's workspace -->
      <div class="worktree-section">
        <div class="worktree-header">
          <span class="worktree-label">Worktree</span>
          {#if card.worktree_path}
            <button class="worktree-unlink" onclick={() => setWorktree(null)} title="Unlink worktree">✕</button>
          {/if}
        </div>
        {#if card.worktree_path}
          <div class="worktree-value" title={card.worktree_path}>
            <span class="worktree-icon" aria-hidden="true">▸</span>
            <span class="worktree-path">{card.worktree_path}</span>
          </div>
          <div class="worktree-actions">
            <button class="worktree-btn primary" onclick={() => onOpenWorktree?.(card!.worktree_path!)}>
              Open in explorer
            </button>
            <button class="worktree-btn" onclick={pickWorktree}>Change</button>
          </div>
        {:else}
          <button class="worktree-btn primary" onclick={pickWorktree}>
            Link a folder…
          </button>
        {/if}
      </div>

      {#if card.source_url}
        <div class="field">
          <span class="field-label">Source</span>
          <a
            class="source-link"
            href={card.source_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {#if card.source_url.includes('linear.app')}
              View in Linear ↗
            {:else if card.source_url.includes('plane.so')}
              View in Plane ↗
            {:else if card.source_url.includes('app.asana.com')}
              View in Asana ↗
            {:else if card.source_url.includes('figma.com')}
              View in Figma ↗
            {:else}
              View Source ↗
            {/if}
          </a>
        </div>
      {/if}

      <div class="meta">
        <span>Created {timeAgo(card.created_at)}</span>
        <span>Updated {timeAgo(card.updated_at)}</span>
      </div>
    {/if}

    {#if drawerTab === 'discussion'}
      <div class="discussion-tab comments-section">
        {#if commentsLoading}
          <p class="loading-hint">Loading discussion…</p>
        {:else if comments.length === 0}
          <p class="empty-hint">No discussion yet. Add the first comment.</p>
        {:else}
          <div class="comment-list">
            {#each comments as comment (comment.id)}
              <div class="comment comment-item">
                <div class="comment-header">
                  <span class="comment-author">{comment.author || 'You'}</span>
                  <span class="comment-time">{formatCommentTime(comment.created_at)}</span>
                  <button class="comment-delete" onclick={() => deleteComment(comment.id)} title="Delete">✕</button>
                </div>
                <p class="comment-body">{comment.body}</p>
              </div>
            {/each}
          </div>
        {/if}

        <div class="add-comment comment-input-area">
          <textarea
            class="textarea-field comment-input comment-textarea"
            bind:value={newCommentBody}
            rows={3}
            placeholder="Add a comment…"
            onkeydown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                void submitComment();
              }
            }}
          ></textarea>
          <button class="btn-comment comment-submit" onclick={submitComment} disabled={submittingComment || !newCommentBody.trim()}>
            {submittingComment ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </div>
    {/if}

    {#if drawerTab === 'runs'}
      <div class="runs-tab">
        {#if card.agent_id && liveOutput}
          <section class="runs-section">
            <h4 class="comments-title runs-section-title">
              <span class="runs-live-dot" aria-hidden="true"></span>
              Live output
            </h4>
            <pre class="transcript-pre live-output">{liveOutput.slice(-2000)}</pre>
          </section>
        {/if}

        <section class="runs-section">
          <h4 class="comments-title runs-section-title">Run history</h4>
          {#if runEvents.length === 0}
            <p class="empty-hint">No agent runs recorded for this card yet.</p>
          {:else}
            <ul class="run-list">
              {#each runEvents as ev (ev.id)}
                <li class="run-row">
                  <span
                    class="run-dot"
                    style="background: {
                      ev.event_type === 'task_completed' ? 'oklch(0.72 0.19 145)' :
                      ev.event_type === 'task_failed' ? 'oklch(0.68 0.24 25)' :
                      ev.event_type === 'task_cancelled' ? 'oklch(0.78 0.16 85)' :
                      ev.event_type === 'task_started' ? 'oklch(0.72 0.28 310)' :
                      'oklch(0.58 0.22 265)'
                    }"
                    aria-hidden="true"
                  ></span>
                  <span class="run-type">{ev.event_type.replace(/^task_/, '').replace(/_/g, ' ')}</span>
                  <span class="run-agent" title={ev.agent_id}>{ev.agent_id.slice(0, 8)}</span>
                  <span class="run-time">{timeAgo(ev.created_at)}</span>
                </li>
              {/each}
            </ul>
          {/if}
        </section>
      </div>
    {/if}

    {#if drawerTab === 'activity'}
      {#if card.source_url}
        <div class="import-banner">
          <span class="import-icon">↓</span>
          <span>Activity history available for imported cards —
            <a class="import-banner-link" href={card.source_url} target="_blank" rel="noopener noreferrer">view in source ↗</a>
          </span>
        </div>
      {/if}

      <div class="activity-tab">
        {#if activityLoading}
          <p class="loading-hint">Loading activity…</p>
        {:else if activityEvents.length === 0}
          <p class="empty-hint">No activity recorded yet.</p>
        {:else}
          <div class="activity-feed">
            {#each [...activityEvents].reverse() as ev (ev.id)}
              <div class="activity-entry">
                <span class="activity-icon">
                  {#if ev.event_type === 'card_created'}
                    <StatusIcon status="done" size={12} />
                  {:else if ev.event_type === 'card_moved'}
                    <StatusIcon status="in progress" size={12} />
                  {:else if ev.event_type === 'task_completed'}
                    <StatusIcon status="done" size={12} />
                  {:else if ev.event_type === 'task_failed'}
                    <StatusIcon status="canceled" size={12} />
                  {:else}
                    <StatusIcon status="todo" size={12} />
                  {/if}
                </span>
                <span class="activity-text">{ev.event_type.replace(/_/g, ' ')}</span>
                <span class="activity-time">{timeAgo(ev.created_at)}</span>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    {/if}

    {#if drawerTab === 'transcript'}
      <div class="transcript-section">
        <h4 class="comments-title">Agent transcript</h4>
        {#if card.agent_id}
          <pre class="transcript-pre">{@html (agentStore?.outputs?.[card.agent_id] ?? []).join('') || 'No output yet. The agent is running — output streams in real time via WebSocket.'}</pre>
        {:else}
          <p class="empty-hint">No transcript yet. Start an agent run to capture output.</p>
        {/if}
      </div>
    {/if}
    </div>
  </aside>
{/if}

<style>
  /* Round 4: split-view drawer. No backdrop — the board stays visible
     and clickable underneath. Click-outside-to-close is handled by a
     window-level pointerdown listener. */
  .panel {
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    /* width is bound inline from drawerWidth */
    background: oklch(0.065 0.015 275);
    border-left: 1px solid oklch(0.30 0.05 275 / 0.30);
    z-index: 50;
    display: flex;
    flex-direction: column;
    /* Soft cast-left shadow to separate from the board — much lighter
       than before so the board remains the visual focus. */
    box-shadow: -16px 0 48px rgba(0, 0, 0, 0.40);
  }
  .panel.resizing {
    user-select: none;
  }

  /* Left-edge drag handle: invisible 6px hit target sitting on the
     border. Shows a subtle purple glow on hover. */
  .resize-handle {
    position: absolute;
    left: -3px;
    top: 0;
    bottom: 0;
    width: 6px;
    cursor: ew-resize;
    z-index: 51;
  }
  .resize-handle::after {
    content: '';
    position: absolute;
    left: 2px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: transparent;
    transition: background-color 100ms ease;
  }
  .resize-handle:hover::after,
  .panel.resizing .resize-handle::after {
    background: oklch(0.72 0.28 310 / 0.55);
  }
  header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.75rem 1.25rem;
    border-bottom: 1px solid rgba(255,255,255,0.05);
    flex-shrink: 0;
    gap: 0.75rem;
  }
  .header-left { flex: 1; min-width: 0; }
  .header-right { display: flex; align-items: center; gap: 0.625rem; flex-shrink: 0; }
  .breadcrumb {
    display: flex; align-items: center; flex-wrap: wrap;
    font-size: 0.68rem; color: rgba(255,255,255,0.2);
    line-height: 1.4;
  }
  .breadcrumb-project { color: rgba(255,255,255,0.25); }
  .breadcrumb-column { color: rgba(255,255,255,0.22); }
  .breadcrumb-sep { color: rgba(255,255,255,0.12); margin: 0 0.15rem; }
  .breadcrumb-id {
    font-family: 'JetBrains Mono', monospace;
    color: rgba(255,255,255,0.18);
    background: rgba(255,255,255,0.04);
    border-radius: 3px;
    padding: 0.05rem 0.3rem;
    font-size: 0.65rem;
  }
  .save-status {
    font-size: 0.65rem;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }
  .save-status.saving { color: rgba(255,255,255,0.25); }
  .save-status.saved  { color: oklch(0.78 0.26 310 / 0.58); }
  .save-status.error  { color: oklch(0.78 0.16 85 / 0.80); }
  .close-btn {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
    color: #666; padding: 0.3rem 0.875rem; border-radius: 5px; cursor: pointer; font-size: 0.8rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .close-btn:hover { background: rgba(255,255,255,0.08); color: #999; }

  /* Round 14: drawer tab bar */
  .drawer-tabs {
    display: flex;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-shrink: 0;
    padding: 0 1.25rem;
  }
  .drawer-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: oklch(0.55 0.05 310 / 0.6);
    padding: 0.5rem 0.65rem;
    font-size: 0.72rem;
    font-weight: 500;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    transition: color 100ms ease, border-color 100ms ease;
  }
  .drawer-tab:hover { color: oklch(0.85 0.10 310); }
  .drawer-tab.active {
    color: oklch(0.92 0.12 310);
    border-bottom-color: oklch(0.78 0.26 310 / 0.70);
  }
  .tab-count {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.62rem;
    color: oklch(0.78 0.16 85 / 0.80);
    background: oklch(0.78 0.16 85 / 0.10);
    border: 1px solid oklch(0.78 0.16 85 / 0.22);
    border-radius: 999px;
    padding: 0.05rem 0.3rem;
    line-height: 1.2;
  }
  .tab-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .running-dot {
    background: oklch(0.72 0.19 145);
    box-shadow: 0 0 8px oklch(0.72 0.19 145 / 0.50);
  }

  .panel-body {
    flex: 1; overflow-y: auto; padding: 1.25rem; display: flex; flex-direction: column; gap: 1rem;
  }

  /* Improvement #24: AI-assigned badge */
  .ai-assigned-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.68rem;
    color: oklch(0.82 0.18 310 / 0.85);
    background: oklch(0.72 0.28 310 / 0.10);
    border: 1px solid oklch(0.72 0.28 310 / 0.22);
    border-radius: 5px;
    padding: 0.2rem 0.55rem;
    align-self: flex-start;
  }

  .field { display: flex; flex-direction: column; gap: 0.375rem; }
  label { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(255,255,255,0.25); }
  .input-field, .textarea-field {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: #d0d4e0;
    padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.85rem; outline: none; width: 100%;
    box-sizing: border-box; transition: border-color 150ms ease;
  }
  .input-field:focus, .textarea-field:focus { border-color: oklch(0.72 0.28 310 / 0.32); }
  .textarea-field { resize: vertical; line-height: 1.5; font-family: system-ui, sans-serif; }
  .date-input { color-scheme: dark; }
  .priority-picker { display: flex; gap: 0.375rem; flex-wrap: wrap; }
  .priority-option {
    display: flex; align-items: center; gap: 0.35rem;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
    color: rgba(255,255,255,0.35); padding: 0.28rem 0.55rem; border-radius: 4px; font-size: 0.73rem;
    cursor: pointer; transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .priority-option.selected { border-color: var(--dot-color); color: #d0d4e0; background: rgba(255,255,255,0.05); }
  .priority-option:hover:not(.selected) { border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.6); }
  .p-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .column-picker { display: flex; gap: 0.375rem; flex-wrap: wrap; }
  .column-option {
    display: flex; align-items: center; gap: 0.35rem;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
    color: rgba(255,255,255,0.35); padding: 0.28rem 0.6rem; border-radius: 4px; font-size: 0.73rem;
    cursor: pointer; transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .column-option.selected {
    border-color: oklch(0.72 0.28 310 / 0.42); color: #d0d4e0; background: oklch(0.72 0.28 310 / 0.07);
  }
  .column-option:hover:not(.selected) { border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.6); }
  .label-picker { display: flex; gap: 0.35rem; flex-wrap: wrap; }
  .label-option {
    display: flex; align-items: center; gap: 0.35rem;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
    color: rgba(255,255,255,0.4); padding: 0.25rem 0.6rem; border-radius: 12px; font-size: 0.73rem;
    cursor: pointer; transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .label-option.active {
    border-color: var(--label-color);
    color: #e8eaf0;
    background: rgba(255,255,255,0.05);
    box-shadow: 0 0 8px color-mix(in srgb, var(--label-color) 30%, transparent);
  }
  .label-option:hover:not(.active) { border-color: rgba(255,255,255,0.18); color: rgba(255,255,255,0.65); }
  .label-swatch { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .label-check { margin-left: 0.15rem; color: rgba(255,255,255,0.5); font-size: 0.65rem; }
  .agent-link {
    display: flex; align-items: center; gap: 0.5rem;
    background: oklch(0.55 0.20 265 / 0.08); border: 1px solid oklch(0.55 0.20 265 / 0.28);
    color: oklch(0.55 0.20 265 / 0.70); padding: 0.45rem 0.75rem; border-radius: 6px;
    font-size: 0.75rem; font-family: 'JetBrains Mono', monospace; cursor: pointer;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease; text-align: left; width: 100%;
  }
  .agent-link:hover { background: oklch(0.55 0.20 265 / 0.12); color: oklch(0.58 0.22 265); }

  .persona-select { cursor: pointer; color-scheme: dark; }

  .start-work-btn {
    display: flex; align-items: center; gap: 0.5rem;
    background: oklch(0.72 0.28 310 / 0.07);
    border: 1px solid oklch(0.72 0.28 310 / 0.26);
    color: oklch(0.74 0.28 310 / 0.84);
    padding: 0.5rem 0.875rem;
    border-radius: 7px;
    cursor: pointer;
    font-size: 0.82rem;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    width: 100%;
    text-align: left;
    box-sizing: border-box;
  }
  .start-work-btn:hover:not(:disabled) {
    background: oklch(0.72 0.28 310 / 0.14);
    border-color: oklch(0.72 0.28 310 / 0.42);
    box-shadow: 0 0 14px oklch(0.72 0.28 310 / 0.12);
  }
  .start-work-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .sw-icon { font-size: 0.9rem; flex-shrink: 0; }
  .sw-hint { margin-left: auto; font-size: 0.68rem; color: oklch(0.72 0.28 310 / 0.42); font-family: 'JetBrains Mono', monospace; }

  .focus-btn {
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.3); padding: 0.28rem 0.5rem; border-radius: 5px;
    cursor: pointer; font-size: 0.85rem; transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease; line-height: 1;
  }
  .focus-btn:hover { background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.6); }
  .agent-pulse {
    width: 6px; height: 6px; border-radius: 50%; background: oklch(0.58 0.22 265);
    box-shadow: 0 0 6px oklch(0.55 0.20 265 / 0.70); animation: pulse 1.5s ease-in-out infinite; flex-shrink: 0;
  }
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
  .snoop-arrow { margin-left: auto; color: oklch(0.55 0.20 265 / 0.50); }
  .meta { display: flex; flex-direction: column; gap: 0.2rem; padding-top: 0.5rem; border-top: 1px solid rgba(255,255,255,0.04); }
  .meta span { font-size: 0.68rem; color: rgba(255,255,255,0.18); }

  .activity-feed {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 0.5rem 0 0 0.5rem;
    border-left: 1px solid oklch(0.30 0.05 275 / 0.3);
    margin-left: 0.35rem;
  }
  .activity-entry {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.72rem;
    color: oklch(0.72 0.04 310 / 0.70);
  }
  .activity-icon { flex-shrink: 0; }
  .activity-text { flex: 1; min-width: 0; }
  .activity-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.62rem;
    color: oklch(0.50 0.04 310 / 0.50);
    flex-shrink: 0;
  }

  /* ── Round 14: Agent transcript ──────────────────── */
  .transcript-section {
    border-top: 1px solid rgba(255,255,255,0.04);
    padding-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .transcript-pre {
    background: oklch(0.065 0.015 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    border-radius: 4px;
    padding: 0.65rem 0.75rem;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.68rem;
    color: oklch(0.80 0.04 310 / 0.80);
    line-height: 1.55;
    max-height: 300px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
  }

  .comments-section {
    display: flex; flex-direction: column; gap: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(255,255,255,0.05);
  }
  .discussion-tab {
    padding-top: 0;
    border-top: none;
  }
  .comments-title {
    font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.08em;
    color: rgba(255,255,255,0.25); margin: 0;
  }
  .comment-list { display: flex; flex-direction: column; gap: 0.625rem; }
  .comment {
    background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.05);
    border-radius: 6px; padding: 0.6rem 0.75rem;
  }
  .comment-header { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.35rem; }
  .comment-author { font-size: 0.72rem; color: oklch(0.80 0.26 310 / 0.68); font-weight: 600; }
  .comment-time { font-size: 0.65rem; color: rgba(255,255,255,0.2); font-family: 'JetBrains Mono', monospace; }
  .comment-delete {
    margin-left: auto; background: none; border: none; color: rgba(255,255,255,0.15);
    cursor: pointer; font-size: 0.65rem; padding: 0.1rem 0.2rem; border-radius: 3px;
    transition: color 150ms ease;
  }
  .comment-delete:hover { color: rgba(255,80,80,0.6); }
  .comment-body { font-size: 0.8rem; color: #aab0c0; line-height: 1.5; margin: 0; white-space: pre-wrap; }
  .add-comment { display: flex; flex-direction: column; gap: 0.5rem; }
  .comment-input { font-size: 0.8rem; }
  .comment-input-area {
    margin-top: 0.25rem;
  }
  .btn-comment {
    align-self: flex-end;
    background: oklch(0.55 0.20 265 / 0.10); border: 1px solid oklch(0.55 0.20 265 / 0.28);
    color: oklch(0.55 0.20 265 / 0.70); padding: 0.3rem 0.875rem; border-radius: 5px; font-size: 0.8rem;
    cursor: pointer; transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .btn-comment:hover:not(:disabled) { background: oklch(0.55 0.20 265 / 0.18); color: oklch(0.58 0.22 265); box-shadow: 0 0 10px oklch(0.55 0.20 265 / 0.28); }
  .btn-comment:disabled { opacity: 0.4; cursor: not-allowed; }
  .empty-hint { font-size: 0.72rem; color: rgba(255,255,255,0.18); margin: 0; font-style: italic; }
  .loading-hint { font-size: 0.72rem; color: rgba(255,255,255,0.18); margin: 0; }

  .runs-tab {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }
  .runs-section {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }
  .runs-section-title {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
  .runs-live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: oklch(0.72 0.19 145);
    box-shadow: 0 0 8px oklch(0.72 0.19 145 / 0.45);
  }
  .live-output {
    max-height: 220px;
  }
  .run-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
  }
  .run-row {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    gap: 0.55rem;
    align-items: center;
    padding: 0.55rem 0.65rem;
    border-radius: 6px;
    border: 1px solid oklch(0.30 0.05 275 / 0.25);
    background: oklch(0.11 0.02 275);
  }
  .run-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }
  .run-type {
    font-size: 0.76rem;
    color: oklch(0.84 0.05 310 / 0.84);
    text-transform: capitalize;
  }
  .run-agent,
  .run-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.66rem;
    color: oklch(0.58 0.04 310 / 0.68);
  }

  .activity-tab {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-top: 0.25rem;
  }

  .desc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .desc-hint {
    font-size: 0.62rem;
    color: oklch(0.55 0.04 310 / 0.45);
    font-style: italic;
  }
  .markdown-textarea {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.8rem;
    line-height: 1.6;
    resize: vertical;
    min-height: 120px;
  }
  .markdown-view {
    min-height: 60px;
    padding: 0.5rem 0.75rem;
    border-radius: 6px;
    border: 1px solid rgba(255,255,255,0.05);
    cursor: text;
    transition: border-color 150ms ease;
    color: #c0c8d4;
    font-size: 0.85rem;
    line-height: 1.6;
  }
  .markdown-view:hover { border-color: rgba(255,255,255,0.1); }
  .markdown-view.empty .placeholder {
    color: rgba(255,255,255,0.2);
    font-style: italic;
    font-size: 0.8rem;
  }
  .markdown-view :global(h1),
  .markdown-view :global(h2),
  .markdown-view :global(h3) {
    color: #e8eaf0;
    margin: 0.5rem 0 0.25rem;
    line-height: 1.3;
  }
  .markdown-view :global(h1) { font-size: 1.1rem; }
  .markdown-view :global(h2) { font-size: 0.95rem; }
  .markdown-view :global(h3) { font-size: 0.875rem; }
  .markdown-view :global(p) { margin: 0 0 0.5rem; }
  .markdown-view :global(code) {
    background: rgba(255,255,255,0.07);
    border-radius: 3px;
    padding: 0.05rem 0.35rem;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.78em;
    color: #88d0a0;
  }
  .markdown-view :global(pre) {
    background: rgba(0,0,0,0.3);
    border-radius: 5px;
    padding: 0.75rem;
    overflow-x: auto;
    margin: 0.5rem 0;
  }
  .markdown-view :global(pre code) { background: none; padding: 0; font-size: 0.8rem; }
  .markdown-view :global(ul),
  .markdown-view :global(ol) { padding-left: 1.25rem; margin: 0.25rem 0; }
  .markdown-view :global(li) { margin: 0.15rem 0; color: #b8bfcc; }
  .markdown-view :global(blockquote) {
    border-left: 3px solid oklch(0.72 0.28 310 / 0.36);
    padding: 0.25rem 0.75rem;
    margin: 0.5rem 0;
    color: rgba(255,255,255,0.45);
    font-style: italic;
  }
  .markdown-view :global(a) { color: #4d96ff; text-decoration: none; }
  .markdown-view :global(a:hover) { text-decoration: underline; }
  .markdown-view :global(hr) {
    border: none;
    border-top: 1px solid rgba(255,255,255,0.08);
    margin: 0.75rem 0;
  }
  .markdown-view :global(strong) { color: #e8eaf0; font-weight: 600; }
  .markdown-view :global(em) { color: rgba(255,255,255,0.7); }

  .loop-section { padding: 0.75rem 0; border-top: 1px solid var(--border-faint, rgba(255,255,255,0.06)); }
  .loop-toggle-label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; font-size: 0.8rem; color: var(--text-muted, rgba(255,255,255,0.5)); }
  .loop-checkbox { accent-color: oklch(0.80 0.26 310); cursor: pointer; }
  .loop-label-text { user-select: none; }
  .loop-count-badge { font-family: monospace; font-size: 0.7rem; background: oklch(0.72 0.28 310 / 0.12); color: oklch(0.80 0.26 310); border-radius: 999px; padding: 0.1rem 0.4rem; }
  .loop-details { margin-top: 0.5rem; display: flex; flex-direction: column; gap: 0.375rem; }
  .loop-field-label { font-size: 0.65rem; text-transform: uppercase; letter-spacing: 0.08em; color: rgba(255,255,255,0.3); }
  .loop-max-input { width: 4rem; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; color: #e8eaf0; padding: 0.2rem 0.4rem; font-size: 0.8rem; outline: none; }
  .loop-criteria-input { width: 100%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 4px; color: #d0d4e0; padding: 0.4rem 0.5rem; font-size: 0.8rem; resize: vertical; font-family: system-ui; outline: none; box-sizing: border-box; }
  .loop-max-input:focus, .loop-criteria-input:focus { border-color: oklch(0.72 0.28 310 / 0.32); }

  /* ── Worktree link section ──────────────────────────────────────── */
  .worktree-section {
    padding: 0.75rem 0;
    border-top: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .worktree-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .worktree-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: oklch(0.72 0.08 310 / 0.6);
    font-weight: 600;
  }
  .worktree-unlink {
    background: transparent;
    border: none;
    color: oklch(0.55 0.05 310 / 0.5);
    cursor: pointer;
    font-size: 0.8rem;
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
    transition: background-color 100ms ease, color 100ms ease;
  }
  .worktree-unlink:hover {
    color: oklch(0.72 0.22 25);
    background: oklch(0.62 0.26 25 / 0.10);
  }
  .worktree-value {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 0.55rem;
    background: oklch(0.14 0.02 275 / 0.50);
    border: 1px solid oklch(0.30 0.05 275 / 0.20);
    border-radius: 5px;
    overflow: hidden;
  }
  .worktree-icon {
    color: oklch(0.76 0.18 85 / 0.85);
    flex-shrink: 0;
    font-size: 0.78rem;
  }
  .worktree-path {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.72rem;
    color: oklch(0.85 0.06 310);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    direction: rtl; /* truncate from the left so the tail of the path stays visible */
    text-align: left;
  }
  .worktree-actions {
    display: flex;
    gap: 0.4rem;
  }
  .worktree-btn {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    color: oklch(0.72 0.03 310 / 0.75);
    padding: 0.35rem 0.75rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.72rem;
    font-weight: 500;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
  }
  .worktree-btn:hover {
    background: rgba(255,255,255,0.08);
    color: oklch(0.90 0.04 310);
    border-color: rgba(255,255,255,0.18);
  }
  .worktree-btn.primary {
    background: oklch(0.72 0.28 310 / 0.12);
    border-color: oklch(0.72 0.28 310 / 0.32);
    color: oklch(0.86 0.20 310);
  }
  .worktree-btn.primary:hover {
    background: oklch(0.72 0.28 310 / 0.22);
    border-color: oklch(0.80 0.26 310 / 0.50);
    color: oklch(0.94 0.15 310);
  }

  /* ── Source provenance link ─────────────────────────────────────── */
  .field-label {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255,255,255,0.25);
  }
  .source-link {
    display: inline-flex;
    align-items: center;
    font-size: 0.78rem;
    color: oklch(0.62 0.20 265 / 0.85);
    text-decoration: none;
    border: 1px solid oklch(0.55 0.20 265 / 0.25);
    background: oklch(0.55 0.20 265 / 0.07);
    padding: 0.3rem 0.65rem;
    border-radius: 5px;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    width: fit-content;
  }
  .source-link:hover {
    background: oklch(0.55 0.20 265 / 0.14);
    color: oklch(0.70 0.22 265);
    border-color: oklch(0.55 0.20 265 / 0.45);
  }

  /* ── Import banner (Activity tab) ───────────────────────────────── */
  .import-banner {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.75rem;
    background: oklch(0.55 0.20 265 / 0.07);
    border: 1px solid oklch(0.55 0.20 265 / 0.22);
    border-radius: 6px;
    font-size: 0.75rem;
    color: oklch(0.72 0.08 310 / 0.70);
    margin-bottom: 0.25rem;
  }
  .import-icon {
    font-size: 0.85rem;
    color: oklch(0.62 0.20 265 / 0.80);
    flex-shrink: 0;
  }
  .import-banner-link {
    color: oklch(0.62 0.20 265 / 0.90);
    text-decoration: none;
  }
  .import-banner-link:hover {
    text-decoration: underline;
    color: oklch(0.72 0.22 265);
  }
</style>
